import moment from 'moment-timezone';
import { isNotNullOrUndefined, isNumberGreaterThanZero, isObjectNotEmpty } from '../utils/objectUtils';

class WeightSnapshot {
    startingWeight = null; // first weight ever recorded, in lbs
    startingWeightAt = null;
    currentWeight = null; // most recently recorded weight in lbs
    currentWeightAt = null;
    previousWeight = null; // the weight recorded just before the current one
    previousWeightAt = null;
    lowestWeight = null;
    lowestWeightAt = null;
    highestWeight = null;
    highestWeightAt = null;

    // a weight is only ever a positive number here: 0 is the User model's "no weight yet" default, never a
    // measurement, so a stored 0 reads as absent and can never become a starting or lowest weight
    constructor(weightSnapshot) {
        if (isObjectNotEmpty(weightSnapshot)) {
            if (isNumberGreaterThanZero(weightSnapshot.startingWeight)) { this.startingWeight = weightSnapshot.startingWeight; }
            if (isNotNullOrUndefined(weightSnapshot.startingWeightAt)) { this.startingWeightAt = weightSnapshot.startingWeightAt; }
            if (isNumberGreaterThanZero(weightSnapshot.currentWeight)) { this.currentWeight = weightSnapshot.currentWeight; }
            if (isNotNullOrUndefined(weightSnapshot.currentWeightAt)) { this.currentWeightAt = weightSnapshot.currentWeightAt; }
            if (isNumberGreaterThanZero(weightSnapshot.previousWeight)) { this.previousWeight = weightSnapshot.previousWeight; }
            if (isNotNullOrUndefined(weightSnapshot.previousWeightAt)) { this.previousWeightAt = weightSnapshot.previousWeightAt; }
            if (isNumberGreaterThanZero(weightSnapshot.lowestWeight)) { this.lowestWeight = weightSnapshot.lowestWeight; }
            if (isNotNullOrUndefined(weightSnapshot.lowestWeightAt)) { this.lowestWeightAt = weightSnapshot.lowestWeightAt; }
            if (isNumberGreaterThanZero(weightSnapshot.highestWeight)) { this.highestWeight = weightSnapshot.highestWeight; }
            if (isNotNullOrUndefined(weightSnapshot.highestWeightAt)) { this.highestWeightAt = weightSnapshot.highestWeightAt; }
        }
    }

    toDto = () => {
        return {
            startingWeight: this.startingWeight,
            startingWeightAt: this.startingWeightAt,
            currentWeight: this.currentWeight,
            currentWeightAt: this.currentWeightAt,
            previousWeight: this.previousWeight,
            previousWeightAt: this.previousWeightAt,
            lowestWeight: this.lowestWeight,
            lowestWeightAt: this.lowestWeightAt,
            highestWeight: this.highestWeight,
            highestWeightAt: this.highestWeightAt,
        };
    }

    // folds one recorded weight into the snapshot; weights must be applied in chronological order
    static applyNewWeight = (weightSnapshot, weight, at = null) => {
        let snapshot = new WeightSnapshot(weightSnapshot);
        let value = WeightSnapshot.toNumber(weight);
        if (!isNumberGreaterThanZero(value)) {
            return snapshot.toDto();
        }
        let atValue = isNotNullOrUndefined(at) ? at : moment().utc().toDate();
        if (!isNumberGreaterThanZero(snapshot.startingWeight)) {
            snapshot.startingWeight = value;
            snapshot.startingWeightAt = atValue;
        }
        if (isNumberGreaterThanZero(snapshot.currentWeight)) {
            snapshot.previousWeight = snapshot.currentWeight;
            snapshot.previousWeightAt = snapshot.currentWeightAt;
        }
        snapshot.currentWeight = value;
        snapshot.currentWeightAt = atValue;
        if (!isNumberGreaterThanZero(snapshot.lowestWeight) || value < snapshot.lowestWeight) {
            snapshot.lowestWeight = value;
            snapshot.lowestWeightAt = atValue;
        }
        if (!isNumberGreaterThanZero(snapshot.highestWeight) || value > snapshot.highestWeight) {
            snapshot.highestWeight = value;
            snapshot.highestWeightAt = atValue;
        }
        return snapshot.toDto();
    }

    // rebuilds the snapshot from a chronological list of ValueHistory dtos ({ value, by, at })
    static buildFromHistory = (weightHistory = []) => {
        let snapshot = null;
        for (const entry of (weightHistory ?? [])) {
            snapshot = WeightSnapshot.applyNewWeight(snapshot, entry?.value, entry?.at);
        }
        return isObjectNotEmpty(snapshot) ? snapshot : new WeightSnapshot(null).toDto();
    }

    // identical on both sides on purpose, and coerces before any number guard, which rejects "250.0": one decimal
    // place, so 250 and "250.0" are one value and one entry id; anything not a positive number is null
    static toNumber = (value) => {
        const parsed = typeof value === 'number' ? value : parseFloat(value);
        if (!Number.isFinite(parsed) || parsed <= 0) {
            return null;
        }
        const rounded = Math.round(parsed * 10) / 10;
        return rounded > 0 ? rounded : null;
    }
};

export default WeightSnapshot;
